import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Cpu, Globe, Bluetooth } from "lucide-react";
import Seo from "../components/Seo";
import { Section, Eyebrow, FeatureCard } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import DashboardPreview from "../components/DashboardPreview";
import SmartScaleMarquee from "../components/SmartScaleMarquee";
import EagleBackdrop from "../components/EagleBackdrop";
import CTABanner from "../components/CTABanner";
import { CORE_FEATURES, IMAGES } from "../lib/content";
import * as Icons from "lucide-react";

export default function Home() {
  return (
    <div>
      <Seo path="/" />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 md:pt-44">
        <EagleBackdrop position="top" />
        <Section className="relative pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-zinc-400">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> The Complete Fitness Ecosystem
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-7 font-heading text-5xl font-medium leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
                BUILD TODAY.
                <br />
                <span className="text-gold-gradient">RULE TOMORROW.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
                The world's next-generation fitness platform — combining personalized coaching, nutrition,
                workout tracking, smart-scale integration, health insights and connected technology into one complete
                ecosystem.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <Link to="/features" data-testid="hero-learn-more" className="btn-primary">
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/business-partnerships" data-testid="hero-partnerships" className="btn-gold">
                  Business Partnerships
                </Link>
                <Link to="/contact" data-testid="hero-contact" className="btn-secondary">
                  Contact Us
                </Link>
              </div>
            </Reveal>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto mt-16 max-w-4xl"
          >
            <DashboardPreview />
          </motion.div>
        </Section>
      </section>

      {/* Device categories marquee */}
      <Section className="mt-20">
        <Reveal>
          <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">
            Designed to support connected wellness technology
          </p>
        </Reveal>
        <SmartScaleMarquee />
      </Section>

      {/* Value pillars */}
      <Section className="mt-32">
        <Stagger className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Cpu, title: "Personalized by design", desc: "Your data becomes clear, personalized guidance built for your body and goals." },
            { icon: Bluetooth, title: "Connected ecosystem", desc: "Wearables, smart scales and health platforms unified in one hub." },
            { icon: Globe, title: "Built for the world", desc: "A global, accessible platform designed to scale across regions and devices." },
          ].map((p) => (
            <StaggerItem key={p.title}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-8">
                <p.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-xl text-white">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{p.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* Features grid */}
      <Section className="mt-32">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>The Platform</Eyebrow>
            <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight text-white md:text-5xl">
              One ecosystem. <span className="text-white-gradient">Every insight.</span>
            </h2>
            <p className="mt-5 text-base text-zinc-400">
              Every workout, meal, step, measurement and health signal working together — seamlessly.
            </p>
          </Reveal>
        </div>
        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CORE_FEATURES.slice(0, 6).map((f) => {
            const Icon = Icons[f.icon] || Icons.Sparkles;
            return (
              <StaggerItem key={f.title}>
                <FeatureCard icon={Icon} title={f.title} desc={f.desc} to={`/${f.slug}`} testid={`home-feature-${f.slug}`} />
              </StaggerItem>
            );
          })}
        </Stagger>
        <div className="mt-10">
          <Link to="/features" className="btn-secondary" data-testid="home-view-all-features">
            View all features <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      {/* Coaching highlight */}
      <Section className="mt-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Personalized Coaching</Eyebrow>
            <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight text-white md:text-5xl">
              Coaching built around you
            </h2>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              Jollof Fit Kingdom's personalized coaching learns from your training, nutrition, sleep and body
              composition to deliver guidance that adapts to you. It's not a generic plan — it's built around you.
            </p>
            <ul className="mt-7 space-y-3">
              {["Adaptive training and nutrition plans", "Real-time performance and recovery guidance", "Personalized insights that anticipate your needs"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-zinc-300">
                  <ShieldCheck className="h-4 w-4 text-gold" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/coaching" className="btn-gold mt-8" data-testid="home-coaching-cta">
              Explore Coaching <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={IMAGES.eagle} alt="Jollof Fit Kingdom — leadership, focus and excellence" className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-[#050505]/40" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </Section>

      <CTABanner />
    </div>
  );
}
